import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  MenuItem,
  Card,
  CardContent,
  Snackbar,
  Alert,
  FormControlLabel,
  RadioGroup,
  FormControl,
  Radio,
  FormHelperText,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import axiosInstance from "../AxiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import { trackUnitAdded, trackSupplierLinkedToUnit, trackUnitConversionSet } from '../services/analytics';

const ADD_NEW_SUPPLIER_VALUE = "__add_new_supplier__";
const ADD_UNIT_DRAFT_KEY = "add_unit_draft";
const ADD_UNIT_LAST_SUPPLIER_KEY = "last_created_supplier_id_add_unit";

const AddUnit = () => {
  const [product_id, setProductId] = useState("");
  const [buying_unit_type, setBuyingUnitType] = useState("");
  const [selling_unit_type, setSellingUnitType] = useState("");
  const [existingUnits, setExistingUnits] = useState([]);
  const [productUnitTypes, setProductUnitTypes] = useState([]); // Store unit types for selected product only
  const [searchUnitTypes, setSearchUnitTypes] = useState([]); // Store unit types from all users for search functionality
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Unit uniqueness validation states
  const [unitTypeError, setUnitTypeError] = useState("");
  const [buyingUnitError, setBuyingUnitError] = useState(false);
  const [sellingUnitError, setSellingUnitError] = useState(false);
  const buyingUnitRef = useRef(null);
  const sellingUnitRef = useRef(null);
  const [newUnitType, setNewUnitType] = useState("");
  const [selectedExistingUnit, setSelectedExistingUnit] = useState("");
  const [conversionRate, setConversionRate] = useState("");
  const [isAddingNewUnit, setIsAddingNewUnit] = useState(false);
  const [unitCategory, setUnitCategory] = useState("buying"); // Add state to handle buying or selling radio button
  const [addAnotherDialogOpen, setAddAnotherDialogOpen] = useState(false);
  const [fromProductFlow, setFromProductFlow] = useState(false);
  const [currentProductInfo, setCurrentProductInfo] = useState(null);
  const [firstTimeBuyingUnit, setFirstTimeBuyingUnit] = useState(""); // Track first buying unit name
  const [firstTimeSellingUnit, setFirstTimeSellingUnit] = useState(""); // Track first selling unit name
  // Suppliers linkage
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]); // string[]
  const [primarySupplierId, setPrimarySupplierId] = useState("");
  const [supplierError, setSupplierError] = useState("");
  const [addSupplierDialogOpen, setAddSupplierDialogOpen] = useState(false);

  // New state variables for pricing
  const [retailPrice, setRetailPrice] = useState("");
  const [orderPrice, setOrderPrice] = useState("");
  const [profitWarningOpen, setProfitWarningOpen] = useState(false);
  const [calculatedProfitMargin, setCalculatedProfitMargin] = useState(0);
  const [pendingFormData, setPendingFormData] = useState(null);

  // Form field disabling state
  const [formFieldsDisabled, setFormFieldsDisabled] = useState(false);

  // Search functionality states
  const [searchResults, setSearchResults] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [productName, setProductName] = useState("");
  const productsLoading = false; // Constant since we don't load all products

  // Refs for search functionality
  const justSelectedRef = useRef(false);
  const blurTimeoutRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Clear error states when user types in unit fields
  const handleBuyingUnitChange = (event, newValue) => {
    setBuyingUnitType(newValue || "");
    if (buyingUnitError) {
      setBuyingUnitError(false);
      if (!sellingUnitError) setUnitTypeError("");
    }
  };

  const handleSellingUnitChange = (event, newValue) => {
    setSellingUnitType(newValue || "");
    if (sellingUnitError) {
      setSellingUnitError(false);
      if (!buyingUnitError) setUnitTypeError("");
    }
  };

  const handleNewUnitTypeChange = (event, newValue) => {
    setNewUnitType(newValue || "");
    if (buyingUnitError) {
      setBuyingUnitError(false);
      setUnitTypeError("");
    }
  };

  // Function to fetch all unit types for a product from all users (for search functionality)
  const fetchSearchUnitTypes = async (productName) => {
    try {
      console.log("🔍 FETCHING SEARCH UNIT TYPES for product:", productName);
      console.log(
        "🔍 Making API call to:",
        `/units/search/product/${encodeURIComponent(productName)}`,
      );

      const response = await axiosInstance.get(
        `/units/search/product/${encodeURIComponent(productName)}`,
      );
      console.log("📡 Search units API response:", response.data);

      if (response.data.success) {
        // Filter out any null, undefined, or empty values to prevent Autocomplete errors
        const cleanUnitTypes = (response.data.unitTypes || []).filter(
          (unitType) =>
            unitType && typeof unitType === "string" && unitType.trim() !== "",
        );
        console.log("✅ SUCCESS: Setting searchUnitTypes to:", cleanUnitTypes);
        setSearchUnitTypes(cleanUnitTypes);
      } else {
        console.log("❌ API returned success=false");
        setSearchUnitTypes([]);
      }
    } catch (error) {
      console.error("💥 Error fetching search unit types:", error);
      console.error("💥 Error response:", error.response?.data);
      setSearchUnitTypes([]);
    }
  };

  // Remove the products loading useEffect entirely since we only use search now
  // useEffect(() => {
  //   const fetchProducts = async () => {
  //     try {
  //       setProductsLoading(true); // Set loading to true before fetching
  //       const response = await axiosInstance.get('/products');
  //       setProducts(response.data);
  //     } catch (error) {
  //       console.error('Error fetching products:', error);
  //     } finally {
  //       setProductsLoading(false); // Set loading to false after fetching
  //     }
  //   };

  //   fetchProducts();
  // }, []);

  // Search functionality for products when there are more than 5
  useEffect(() => {
    const updateSearchResults = async () => {
      // Enable search immediately without waiting for products.length check
      if (productName.length > 2 && isOnline && !justSelectedRef.current) {
        try {
          console.log(`🔍 Searching for products with query: "${productName}"`);
          const response = await axiosInstance.get(
            `/products/search?q=${productName}`,
          );
          console.log("🔍 Raw search response:", response.data);

          const uniqueResults = response.data.reduce((acc, product) => {
            const key = `${product.product_name}-${product.variety || ""}-${product.brand || ""}-${product.size || ""}`;
            if (!acc[key]) {
              acc[key] = product;
            }
            return acc;
          }, {});
          setSearchResults(Object.values(uniqueResults));
          console.log(
            `✅ Processed ${Object.values(uniqueResults).length} unique results`,
          );
        } catch (error) {
          console.error("❌ Error fetching search results:", error);
          setSearchResults([]);
        }
      } else {
        setSearchResults([]);
      }

      // Reset the justSelected flag after the effect runs
      if (justSelectedRef.current) {
        justSelectedRef.current = false;
      }
    };

    updateSearchResults();
  }, [productName, isOnline]); // Remove products.length dependency

  // Network status handling
  useEffect(() => {
    // Fetch user's suppliers for dropdown
    const fetchSuppliers = async () => {
      try {
        const res = await axiosInstance.get("/suppliers");
        const raw = res.data || [];
        const nonMarket = raw.filter((s) => !s.market_name);
        setSuppliers(nonMarket);
      } catch (e) {
        console.warn("Failed to fetch suppliers:", e);
        setSuppliers([]);
      }
    };
    fetchSuppliers();
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      // Clear any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Get the product ID from query params if available
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const productIdFromParams = queryParams.get("product_id");
    const fromProduct = queryParams.get("from_product") === "true";

    if (productIdFromParams) {
      // Fetch the specific product to validate and set the product name
      const fetchProductById = async () => {
        try {
          const response = await axiosInstance.get(
            `/products/${productIdFromParams}`,
          );
          const product = response.data;
          setProductId(productIdFromParams);
          setProductName(formatProductDisplay(product));
        } catch (error) {
          console.warn(
            "⚠️ Product ID from URL does not exist:",
            productIdFromParams,
          );
        }
      };
      fetchProductById();
    }
    if (fromProduct) {
      setFromProductFlow(true);
    }
  }, [location]); // Remove products dependency

  // Restore draft and newly created supplier when returning from Add Supplier
  useEffect(() => {
    const draftJson = sessionStorage.getItem(ADD_UNIT_DRAFT_KEY);
    if (!draftJson) {
      return;
    }

    try {
      const draft = JSON.parse(draftJson);

      setProductId(draft.product_id || "");
      setProductName(draft.productName || "");
      setBuyingUnitType(draft.buying_unit_type || "");
      setSellingUnitType(draft.selling_unit_type || "");
      setNewUnitType(draft.newUnitType || "");
      setSelectedExistingUnit(draft.selectedExistingUnit || "");
      setConversionRate(draft.conversionRate || "");
      setIsAddingNewUnit(!!draft.isAddingNewUnit);
      setUnitCategory(draft.unitCategory || "buying");
      setRetailPrice(draft.retailPrice || "");
      setOrderPrice(draft.orderPrice || "");
      setFromProductFlow(!!draft.fromProductFlow);
      setFirstTimeBuyingUnit(draft.firstTimeBuyingUnit || "");
      setFirstTimeSellingUnit(draft.firstTimeSellingUnit || "");

      const baseSelected = draft.selectedSupplierIds || [];
      const newSupplierId = sessionStorage.getItem(ADD_UNIT_LAST_SUPPLIER_KEY);

      if (newSupplierId) {
        const merged = baseSelected.includes(newSupplierId)
          ? baseSelected
          : [...baseSelected, newSupplierId];
        setSelectedSupplierIds(merged);
        setPrimarySupplierId(newSupplierId);
      } else {
        setSelectedSupplierIds(baseSelected);
        setPrimarySupplierId(draft.primarySupplierId || "");
      }
    } catch (e) {
      console.warn("Failed to restore AddUnit draft from sessionStorage", e);
    } finally {
      sessionStorage.removeItem(ADD_UNIT_DRAFT_KEY);
      sessionStorage.removeItem(ADD_UNIT_LAST_SUPPLIER_KEY);
    }
  }, []);

  // Handle product selection for searchable field
  const handleSelectProduct = (product) => {
    console.log("🔍 handleSelectProduct called with:", product);

    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    justSelectedRef.current = true; // Set flag to prevent immediate search

    // Check if product has the expected fields
    console.log("📦 Product object structure:", Object.keys(product));
    console.log("🔑 Product ID:", product.product_id);

    const productId = product.product_id;

    if (!productId) {
      console.error("❌ Product missing ID field:", product);
      setSnackbarMessage("Error: Product selection failed - missing ID");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Set the product ID and name
    setProductId(productId.toString());
    setProductName(formatProductDisplay(product));

    // Clear search results
    setSearchResults([]);
  };

  // Handle product name change for searchable field
  const handleProductNameChange = (value) => {
    setProductName(value);
    // Clear product_id if user is typing a new search
    if (product_id) {
      setProductId("");
    }
  };

  // Handle blur for searchable field
  const handleProductNameBlur = () => {
    // Clear search results after a small delay to allow clicking on results
    blurTimeoutRef.current = setTimeout(() => {
      setSearchResults([]);
    }, 150);
  };

  // Handle focus for searchable field
  const handleProductNameFocus = () => {
    // Clear any pending blur timeout when field gets focus again
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  // Render product selection component based on product count
  const renderProductSelection = () => {
    // Show loading state while products are being fetched
    if (productsLoading) {
      return (
        <TextField
          label="Loading products..."
          variant="outlined"
          fullWidth
          disabled
          helperText="Please wait while products are being loaded..."
        />
      );
    }

    // Always show searchable field once products are loaded
    return (
      <Box>
        <TextField
          label="Select Product"
          variant="outlined"
          fullWidth
          value={productName}
          onChange={(e) => handleProductNameChange(e.target.value)}
          onBlur={handleProductNameBlur}
          onFocus={handleProductNameFocus}
          required
          disabled={formFieldsDisabled}
          helperText="Start typing to search products..."
        />
        <List>
          {searchResults.map((product, resultIndex) => (
            <ListItem
              button
              key={resultIndex}
              onClick={() => handleSelectProduct(product)}
            >
              <ListItemText
                primary={product.product_name}
                secondary={`${product.brand ? `Brand: ${product.brand}` : "No brand"}${product.variety ? ` | Variety: ${product.variety}` : " | No variety"}${product.size ? ` | Size: ${product.size}` : ""}`}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  // Fetch units based on the selected product
  useEffect(() => {
    // Only proceed if we have a product_id
    if (product_id) {
      const fetchUnits = async () => {
        try {
          // Fetch the selected product to get its details
          const productResponse = await axiosInstance.get(
            `/products/${product_id}`,
          );
          const selectedProduct = productResponse.data;

          console.log("🎯 SELECTED PRODUCT:", selectedProduct);
          console.log(
            "🔍 SEARCHING FOR UNITS with product name:",
            selectedProduct.product_name,
          );

          // Fetch search unit types from all users for this product (for autocomplete suggestions)
          await fetchSearchUnitTypes(selectedProduct.product_name);

          // Fetch units for this specific product
          const unitsResponse = await axiosInstance.get(
            `/units/product/${product_id}`,
          );
          const specificProductUnits = unitsResponse.data;

          setExistingUnits(specificProductUnits);
          // Pre-populate suppliers from an existing buying unit if available
          const existingBuyingWithSupplier = specificProductUnits.find(
            (u) =>
              u.unit_category === "buying" &&
              (u.supplier_id || (u.supplier_ids && u.supplier_ids.length)),
          );
          if (existingBuyingWithSupplier) {
            if (
              Array.isArray(existingBuyingWithSupplier.supplier_ids) &&
              existingBuyingWithSupplier.supplier_ids.length
            ) {
              setSelectedSupplierIds(
                existingBuyingWithSupplier.supplier_ids.map(String),
              );
            } else if (existingBuyingWithSupplier.supplier_id) {
              setSelectedSupplierIds([
                String(existingBuyingWithSupplier.supplier_id),
              ]);
            }
            if (existingBuyingWithSupplier.supplier_id) {
              setPrimarySupplierId(
                String(existingBuyingWithSupplier.supplier_id),
              );
            }
          }

          // Extract unique unit types for this product
          const uniqueUnitTypes = [
            ...new Set(specificProductUnits.map((unit) => unit.unit_type)),
          ];
          setProductUnitTypes(uniqueUnitTypes);

          // Check if units already exist for this specific product
          if (specificProductUnits.length > 0) {
            setIsAddingNewUnit(true); // This means there are existing units, so show the new unit form
          } else {
            setIsAddingNewUnit(false); // No existing units, show the full form for adding both buying and selling units
          }
        } catch (error) {
          console.error("Error fetching product or units:", error);
          // Clear unit types when product not found
          setSearchUnitTypes([]);
          setProductUnitTypes([]);
          setExistingUnits([]);
        }
      };
      fetchUnits();
    } else {
      // Clear unit types when no product is selected
      setProductUnitTypes([]);
      setSearchUnitTypes([]);
      setExistingUnits([]);
    }
  }, [product_id]); // Remove products dependency

  // Calculate profit margin when relevant values change
  useEffect(() => {
    if (retailPrice && orderPrice && conversionRate) {
      const margin = calculateProfitMargin(
        retailPrice,
        orderPrice,
        conversionRate,
      );
      setCalculatedProfitMargin(margin || 0);
    } else {
      setCalculatedProfitMargin(0);
    }
  }, [retailPrice, orderPrice, conversionRate]);

  // Debug: Log when searchUnitTypes changes
  useEffect(() => {
    console.log("🎯 SEARCH UNIT TYPES UPDATED:", searchUnitTypes);
    console.log("🎯 Length:", searchUnitTypes.length);
  }, [searchUnitTypes]);

  // Function to calculate profit margin
  const calculateProfitMargin = (
    retailPriceValue,
    orderPriceValue,
    conversionRateValue,
  ) => {
    const retail = parseFloat(retailPriceValue);
    const order = parseFloat(orderPriceValue);
    const conversion = parseFloat(conversionRateValue);

    if (
      isNaN(retail) ||
      isNaN(order) ||
      isNaN(conversion) ||
      conversion === 0 ||
      order === 0
    ) {
      return null;
    }

    // Cost per selling unit = Order Price / Conversion Rate
    const costPerSellingUnit = order / conversion;

    // Profit margin = (Retail Price - Cost per selling unit) / Cost per selling unit
    const profitMargin = (retail - costPerSellingUnit) / costPerSellingUnit;

    return profitMargin;
  };

  // Helper function to format product display with brand
  const formatProductDisplay = (product) => {
    let display = product.product_name;

    // Add brand in parentheses
    if (product.brand) {
      display += ` (${product.brand})`;
    } else {
      display += ` (No Brand)`;
    }

    // Add variety with dash if it exists
    if (product.variety) {
      display += ` - ${product.variety}`;
    }

    return display;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (
      (!isAddingNewUnit && (!buying_unit_type || !selling_unit_type)) ||
      !conversionRate
    ) {
      setSnackbarMessage("Please enter all required fields");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // For the first-time unit addition, validate pricing if both prices are provided
    if (!isAddingNewUnit && retailPrice && orderPrice && conversionRate) {
      const profitMargin = calculateProfitMargin(
        retailPrice,
        orderPrice,
        conversionRate,
      );

      if (profitMargin !== null && (profitMargin < 0 || profitMargin > 1)) {
        // Store form data and show warning dialog
        const unitData = {
          product_id,
          buying_unit_type,
          selling_unit_type,
          conversion_rate: conversionRate,
          retail_price: retailPrice,
          order_price: orderPrice,
        };
        setPendingFormData(unitData);
        setCalculatedProfitMargin(profitMargin);
        setProfitWarningOpen(true);
        return;
      }
    }

    // For adding new units, validate pricing if both prices are provided
    if (isAddingNewUnit && retailPrice && orderPrice && conversionRate) {
      const profitMargin = calculateProfitMargin(
        retailPrice,
        orderPrice,
        conversionRate,
      );

      if (profitMargin !== null && (profitMargin < 0 || profitMargin > 1)) {
        // Store form data and show warning dialog
        const unitData = {
          product_id,
          newUnitType,
          selectedExistingUnit,
          conversion_rate: conversionRate,
          unitCategory,
          retail_price: retailPrice,
          order_price: orderPrice,
        };
        setPendingFormData(unitData);
        setCalculatedProfitMargin(profitMargin);
        setProfitWarningOpen(true);
        return;
      }
    }

    // Proceed with form submission
    await submitForm();
  };

  const submitForm = async (formData = null) => {
    setFormFieldsDisabled(true);
    try {
      // Construct unit data based on whether it's the first time adding or adding a new unit
      let unitData;

      if (formData) {
        // Use provided form data (from warning dialog)
        unitData = formData;

        // If this is first-time creation from warning dialog, store the unit names
        if (
          !isAddingNewUnit &&
          formData.buying_unit_type &&
          formData.selling_unit_type
        ) {
          setFirstTimeBuyingUnit(formData.buying_unit_type);
          setFirstTimeSellingUnit(formData.selling_unit_type);
        }
      } else if (isAddingNewUnit) {
        // Subsequent unit addition - send data for adding one new unit
        unitData = {
          product_id,
          newUnitType,
          selectedExistingUnit,
          conversion_rate: conversionRate,
          unitCategory, // This is required for subsequent additions
          retail_price: retailPrice || "", // Always send these fields
          order_price: orderPrice || "", // Always send these fields
        };
      } else {
        // First-time unit creation - send data for creating both buying and selling units
        unitData = {
          product_id,
          buying_unit_type,
          selling_unit_type,
          conversion_rate: conversionRate,
          retail_price: retailPrice || "", // Always send retail_price for selling unit
          order_price: orderPrice || "", // Always send order_price for buying unit
          // Note: Don't send unitCategory for first-time creation
        };

        // Store the first-time unit names for the dialog
        setFirstTimeBuyingUnit(buying_unit_type);
        setFirstTimeSellingUnit(selling_unit_type);
      }

      // Check if both units already exist (only in Add Product workflow)
      if (!isAddingNewUnit && fromProductFlow) {
        try {
          console.log(
            "🔍 CHECK-BEFORE-CREATE: Starting check for product_id:",
            product_id,
          );
          console.log(
            "🔍 CHECK-BEFORE-CREATE: Looking for buying_unit_type:",
            buying_unit_type,
            "selling_unit_type:",
            selling_unit_type,
          );

          const existingUnitsResponse = await axiosInstance.get(
            `/units/product/${product_id}`,
          );
          const existingUnits = existingUnitsResponse.data || [];

          console.log(
            "🔍 CHECK-BEFORE-CREATE: Existing units from API:",
            existingUnits,
          );

          const buyingExists = existingUnits.some(
            (unit) =>
              unit.unit_category === "buying" &&
              String(unit.unit_type).toLowerCase() ===
                String(buying_unit_type).toLowerCase(),
          );

          const sellingExists = existingUnits.some(
            (unit) =>
              unit.unit_category === "selling" &&
              String(unit.unit_type).toLowerCase() ===
                String(selling_unit_type).toLowerCase(),
          );

          console.log(
            "🔍 CHECK-BEFORE-CREATE: buyingExists:",
            buyingExists,
            "sellingExists:",
            sellingExists,
          );

          if (buyingExists && sellingExists) {
            console.log(
              "🔍 CHECK-BEFORE-CREATE: Both units exist, showing message and waiting 5 seconds",
            );

            // Show success message
            setSnackbarMessage(
              "Units were already created in a prior attempt, moving on in 5 seconds",
            );
            setSnackbarSeverity("info");
            setSnackbarOpen(true);

            // Wait 5 seconds then show the Add Another Unit dialog
            setTimeout(async () => {
              try {
                const productResponse = await axiosInstance.get(
                  `/products/${product_id}`,
                );
                const selectedProduct = productResponse.data;
                setCurrentProductInfo(selectedProduct);

                // Always show the dialog when coming from product flow (both first time and subsequent)
                setAddAnotherDialogOpen(true);
              } catch (error) {
                console.error("Error fetching product for dialog:", error);
                // Show dialog anyway
                setAddAnotherDialogOpen(true);
              }
            }, 5000);

            return; // Exit without creating
          } else {
            console.log(
              "🔍 CHECK-BEFORE-CREATE: Units do not both exist, proceeding with creation",
            );
          }
        } catch (error) {
          console.warn(
            "Failed to check existing units, proceeding with creation:",
            error,
          );
          // Continue with normal creation if check fails
        }
      }

      // Send the data to the backend
      await axiosInstance.post("/units", unitData);

      // After creating unit(s), if buying unit is involved and suppliers selected, persist suppliers and primary
      const persistSuppliers = async (buyingUnitTypeName) => {
        try {
          // Validate selection
          if (
            selectedSupplierIds.length > 0 &&
            (!primarySupplierId ||
              !selectedSupplierIds.includes(primarySupplierId))
          ) {
            setSupplierError(
              "Please select a Most Recent Supplier from the selected list.",
            );
            return;
          }
          setSupplierError("");
          // Fetch units for this product
          const unitsResp = await axiosInstance.get(
            `/units/product/${product_id}`,
          );
          const units = unitsResp.data || [];
          // Find the buying unit we just created/updated by unit_type match
          const target = units.find(
            (u) =>
              u.unit_category === "buying" &&
              String(u.unit_type).toLowerCase() ===
                String(buyingUnitTypeName).toLowerCase(),
          );
          if (!target) return; // nothing to do
          const unitId = target.unit_id;
          // PUT suppliers
          await axiosInstance.put(`/units/${unitId}/suppliers`, {
            supplier_ids: selectedSupplierIds.map((s) => parseInt(s)),
          });
          // PUT primary (if set)
          if (primarySupplierId) {
            await axiosInstance.put(`/units/${unitId}/primary-supplier`, {
              supplier_id: parseInt(primarySupplierId),
            });
          }
        } catch (e) {
          console.warn(
            "Failed to persist suppliers/primary after creation:",
            e,
          );
        }
      };

      if (isAddingNewUnit) {
        if (unitCategory === "buying") {
          await persistSuppliers(newUnitType);
        }
      } else {
        // First-time creation: buying_unit_type is the buying unit
        await persistSuppliers(buying_unit_type);
      }

      trackUnitAdded(null, unitCategory);
      if (conversionRate) trackUnitConversionSet(product_id);
      if (selectedSupplierIds.length > 0) trackSupplierLinkedToUnit(null, primarySupplierId || selectedSupplierIds[0]);
      // Show success message
      setSnackbarMessage("Unit added successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // If coming from product flow, handle the next step
      if (fromProductFlow) {
        try {
          const productResponse = await axiosInstance.get(
            `/products/${product_id}`,
          );
          const selectedProduct = productResponse.data;
          setCurrentProductInfo(selectedProduct);

          // Always show the dialog when coming from product flow (both first time and subsequent)
          setAddAnotherDialogOpen(true);
        } catch (error) {
          console.error("Error fetching product for dialog:", error);
          // Show dialog anyway
          setAddAnotherDialogOpen(true);
        }
      } else {
        // Reset the form fields for regular flow
        resetForm();
      }
    } catch (error) {
      console.error("Error adding unit:", error);
      setFormFieldsDisabled(false);

      // Handle duplicate unit errors specifically
      if (error.response?.status === 409) {
        const errorData = error.response.data;
        setUnitTypeError(
          errorData.error ||
            "Duplicate Unit Type entered - Please enter a different Unit Type",
        );

        // Set error states and focus based on which field has the duplicate
        if (
          errorData.field === "buying_unit_type" ||
          errorData.field === "newUnitType"
        ) {
          setBuyingUnitError(true);
          setSellingUnitError(false);
          buyingUnitRef.current?.focus();
        } else if (errorData.field === "selling_unit_type") {
          setSellingUnitError(true);
          setBuyingUnitError(false);
          if (!buyingUnitError) sellingUnitRef.current?.focus();
        }

        // Show error message for 6 seconds
        setSnackbarMessage(
          errorData.error ||
            "Duplicate Unit Type entered - Please enter a different Unit Type",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return; // Don't proceed with success flow
      }

      // Handle other errors
      setSnackbarMessage(
        "Error adding unit: " +
          (error.response ? error.response.data.error : error.message),
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const resetForm = () => {
    setFormFieldsDisabled(false);
    setProductId("");
    setProductName("");
    setBuyingUnitType("");
    setSellingUnitType("");
    setNewUnitType("");
    setSelectedExistingUnit("");
    setConversionRate("");
    setIsAddingNewUnit(false);
    setUnitCategory("buying");
    setRetailPrice("");
    setOrderPrice("");
    setCalculatedProfitMargin(0);
    setPendingFormData(null);
    setFirstTimeBuyingUnit("");
    setFirstTimeSellingUnit("");
    setSelectedSupplierIds([]);
    setPrimarySupplierId("");
    setSupplierError("");
    setUnitTypeError("");
    setBuyingUnitError(false);
    setSellingUnitError(false);
  };

  const handleAddAnotherUnit = () => {
    setAddAnotherDialogOpen(false);
    setFormFieldsDisabled(false);
    // Reset only the unit-related fields, keep the product_id
    setBuyingUnitType("");
    setSellingUnitType("");
    setNewUnitType("");
    setSelectedExistingUnit("");
    setConversionRate("");
    setUnitCategory("buying");
    setRetailPrice("");
    setOrderPrice("");
    setCalculatedProfitMargin(0);
    setPendingFormData(null);
    setSelectedSupplierIds([]);
    setPrimarySupplierId("");
    setSupplierError("");

    // Clear unit validation error states
    setUnitTypeError("");
    setBuyingUnitError(false);
    setSellingUnitError(false);

    // Refresh the units data for the current product without reloading the page
    if (product_id) {
      const fetchUnits = async () => {
        try {
          // Fetch the selected product to get its details
          const productResponse = await axiosInstance.get(
            `/products/${product_id}`,
          );
          const selectedProduct = productResponse.data;

          if (selectedProduct) {
            // Fetch search unit types from all users for this product (for autocomplete suggestions)
            await fetchSearchUnitTypes(selectedProduct.product_name);

            // Fetch units for this specific product
            const unitsResponse = await axiosInstance.get(
              `/units/product/${product_id}`,
            );
            const specificProductUnits = unitsResponse.data;

            setExistingUnits(specificProductUnits);

            // Extract unique unit types for this product
            const uniqueUnitTypes = [
              ...new Set(specificProductUnits.map((unit) => unit.unit_type)),
            ];
            setProductUnitTypes(uniqueUnitTypes);

            // Since we now have existing units, set to adding new unit mode
            setIsAddingNewUnit(true);
          }
        } catch (error) {
          console.error("Error fetching units:", error);
        } finally {
          // Re-enable form fields after all async operations complete
          setFormFieldsDisabled(false);
        }
      };
      fetchUnits();
    } else {
      // If no product_id, re-enable form fields immediately
      setFormFieldsDisabled(false);
    }
  };

  const handleFinishAddingUnits = async () => {
    setAddAnotherDialogOpen(false);
    // Fetch the selected product to get its details for navigation
    try {
      const productResponse = await axiosInstance.get(
        `/products/${product_id}`,
      );
      const selectedProduct = productResponse.data;
      if (selectedProduct) {
        navigate(
          `/dashboard/inventories/stock-entry?product_id=${selectedProduct.product_id}&product_name=${encodeURIComponent(selectedProduct.product_name)}&variety=${encodeURIComponent(selectedProduct.variety || "")}`,
        );
      }
    } catch (error) {
      console.error("Error fetching product for navigation:", error);
      // Navigate anyway with just the product_id
      navigate(`/dashboard/inventories/stock-entry?product_id=${product_id}`);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSuppliersChange = (event) => {
    const value = event.target.value || [];

    if (value.includes(ADD_NEW_SUPPLIER_VALUE)) {
      const cleaned = value.filter((v) => v !== ADD_NEW_SUPPLIER_VALUE);
      setSelectedSupplierIds(cleaned);
      setAddSupplierDialogOpen(true);
      return;
    }

    setSelectedSupplierIds(value);
  };

  const handleAddSupplierDialogClose = () => {
    setAddSupplierDialogOpen(false);
  };

  const handleConfirmAddSupplier = () => {
    try {
      const draft = {
        product_id,
        productName,
        buying_unit_type,
        selling_unit_type,
        newUnitType,
        selectedExistingUnit,
        conversionRate,
        isAddingNewUnit,
        unitCategory,
        retailPrice,
        orderPrice,
        selectedSupplierIds,
        primarySupplierId,
        fromProductFlow,
        firstTimeBuyingUnit,
        firstTimeSellingUnit,
      };

      sessionStorage.setItem(ADD_UNIT_DRAFT_KEY, JSON.stringify(draft));
    } catch (e) {
      console.warn("Failed to save AddUnit draft to sessionStorage", e);
    }

    setAddSupplierDialogOpen(false);
    navigate("/dashboard/suppliers/add", { state: { from: "add_unit" } });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          {isAddingNewUnit ? "Add New Unit" : "Add Unit"}
        </Typography>
        <Card>
          <CardContent>
            <form onSubmit={handleFormSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {renderProductSelection()}
                </Grid>
                {product_id && (
                  <>
                    {!isAddingNewUnit ? (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            Buying Information
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl fullWidth error={!!supplierError}>
                            <InputLabel id="suppliers-multi-label">
                              Suppliers (optional, multiple)
                            </InputLabel>
                            <Select
                              labelId="suppliers-multi-label"
                              label="Suppliers (optional, multiple)"
                              multiple
                              value={selectedSupplierIds}
                              onChange={handleSuppliersChange}
                              disabled={formFieldsDisabled}
                              renderValue={(selected) => {
                                const names = suppliers
                                  .filter((s) =>
                                    selected.includes(String(s.supplier_id)),
                                  )
                                  .map(
                                    (s) =>
                                      s.name || s.supplier_name || "Unnamed",
                                  );
                                return names.join(", ");
                              }}
                            >
                              {suppliers.map((s) => (
                                <MenuItem
                                  key={s.supplier_id}
                                  value={String(s.supplier_id)}
                                >
                                  {s.name || s.supplier_name || "Unnamed"}
                                </MenuItem>
                              ))}
                              <MenuItem value={ADD_NEW_SUPPLIER_VALUE}>
                                <em>Add New Supplier...</em>
                              </MenuItem>
                            </Select>
                            {supplierError && (
                              <FormHelperText>{supplierError}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2">
                            Most Recent Supplier
                          </Typography>
                          <RadioGroup
                            value={primarySupplierId}
                            onChange={(e) =>
                              setPrimarySupplierId(e.target.value)
                            }
                            disabled={formFieldsDisabled}
                          >
                            {selectedSupplierIds.length === 0 ? (
                              <FormHelperText>
                                Select at least one supplier to choose a
                                primary.
                              </FormHelperText>
                            ) : (
                              selectedSupplierIds.map((sid) => {
                                const sup = suppliers.find(
                                  (s) => String(s.supplier_id) === sid,
                                );
                                const label = sup
                                  ? (sup.name ||
                                      sup.supplier_name ||
                                      "Unnamed") +
                                    (sup.market_name
                                      ? ` (${sup.market_name})`
                                      : "")
                                  : sid;
                                return (
                                  <FormControlLabel
                                    key={sid}
                                    value={sid}
                                    control={<Radio />}
                                    label={label}
                                  />
                                );
                              })
                            )}
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            freeSolo
                            options={
                              Array.isArray(searchUnitTypes)
                                ? searchUnitTypes
                                : []
                            }
                            value={buying_unit_type}
                            onChange={handleBuyingUnitChange}
                            disabled={formFieldsDisabled}
                            onInputChange={(event, newInputValue) => {
                              setBuyingUnitType(newInputValue);
                              if (buyingUnitError) {
                                setBuyingUnitError(false);
                                if (!sellingUnitError) setUnitTypeError("");
                              }
                            }}
                            onOpen={() => {
                              console.log(
                                "Autocomplete onOpen: Buying Unit Type",
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Buying Unit Type"
                                variant="outlined"
                                fullWidth
                                required
                                error={buyingUnitError}
                                helperText={
                                  buyingUnitError
                                    ? "Duplicate Unit Type entered - Please enter a different Unit Type"
                                    : "Search from all buying and selling units used by any user for this product"
                                }
                                inputRef={buyingUnitRef}
                                inputProps={{
                                  ...params.inputProps,
                                  "data-testid": "unit-form-buying-type",
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Current Order Price of Buying Unit"
                            variant="outlined"
                            fullWidth
                            type="number"
                            inputProps={{
                              step: "0.01",
                              min: "0",
                              "data-testid": "unit-form-order-price",
                            }}
                            value={orderPrice}
                            onChange={(e) => setOrderPrice(e.target.value)}
                            disabled={formFieldsDisabled}
                            helperText={`Price per ${buying_unit_type || "buying unit"}`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            Selling Information
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            freeSolo
                            options={
                              Array.isArray(searchUnitTypes)
                                ? searchUnitTypes
                                : []
                            }
                            value={selling_unit_type}
                            onChange={handleSellingUnitChange}
                            disabled={formFieldsDisabled}
                            onInputChange={(event, newInputValue) => {
                              setSellingUnitType(newInputValue);
                              if (sellingUnitError) {
                                setSellingUnitError(false);
                                if (!buyingUnitError) setUnitTypeError("");
                              }
                            }}
                            onOpen={() => {
                              console.log(
                                "Autocomplete onOpen: Selling Unit Type",
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Selling Unit Type"
                                variant="outlined"
                                fullWidth
                                required
                                error={sellingUnitError}
                                helperText={
                                  sellingUnitError
                                    ? "Duplicate Unit Type entered - Please enter a different Unit Type"
                                    : "Search from all buying and selling units used by any user for this product"
                                }
                                inputRef={sellingUnitRef}
                                inputProps={{
                                  ...params.inputProps,
                                  "data-testid": "unit-form-selling-type",
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Current Retail Price of Selling Unit"
                            variant="outlined"
                            fullWidth
                            type="number"
                            inputProps={{
                              step: "0.01",
                              min: "0",
                              "data-testid": "unit-form-retail-price",
                            }}
                            value={retailPrice}
                            onChange={(e) => setRetailPrice(e.target.value)}
                            disabled={formFieldsDisabled}
                            helperText={`Price per ${selling_unit_type || "selling unit"}`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label={`Please Enter How many ${selling_unit_type || "second unit type"} are there in ${buying_unit_type || "first unit type"}?`}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{
                              style: {
                                whiteSpace: "normal",
                                fontSize: "0.875rem", // Reduce the font size to fit the placeholder
                              },
                            }}
                            value={conversionRate}
                            onChange={(e) => setConversionRate(e.target.value)}
                            required
                            disabled={formFieldsDisabled}
                            inputProps={{
                              "data-testid": "unit-form-conversion-rate",
                            }}
                          />
                        </Grid>

                        {/* Real-time Profit Margin Display */}
                        {retailPrice && orderPrice && conversionRate && (
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor:
                                  calculatedProfitMargin !== null &&
                                  (calculatedProfitMargin < 0 ||
                                    calculatedProfitMargin > 1)
                                    ? "warning.light"
                                    : "info.light",
                                borderRadius: 1,
                                border: 1,
                                borderColor:
                                  calculatedProfitMargin !== null &&
                                  (calculatedProfitMargin < 0 ||
                                    calculatedProfitMargin > 1)
                                    ? "warning.main"
                                    : "info.main",
                              }}
                            >
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>Profit Margin Calculation:</strong>
                              </Typography>
                              <Typography variant="body2">
                                Cost per {selling_unit_type || "selling unit"}:{" "}
                                {orderPrice && conversionRate
                                  ? (
                                      parseFloat(orderPrice) /
                                      parseFloat(conversionRate)
                                    ).toFixed(2)
                                  : "N/A"}
                              </Typography>
                              <Typography variant="body2">
                                Profit per {selling_unit_type || "selling unit"}
                                :{" "}
                                {retailPrice && orderPrice && conversionRate
                                  ? (
                                      parseFloat(retailPrice) -
                                      parseFloat(orderPrice) /
                                        parseFloat(conversionRate)
                                    ).toFixed(2)
                                  : "N/A"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                Profit Margin:{" "}
                                {calculatedProfitMargin !== null
                                  ? `${(calculatedProfitMargin * 100).toFixed(1)}%`
                                  : "N/A"}
                              </Typography>
                              {calculatedProfitMargin !== null &&
                                calculatedProfitMargin < 0 && (
                                  <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ mt: 1 }}
                                  >
                                    ⚠️ This results in a loss
                                  </Typography>
                                )}
                              {calculatedProfitMargin !== null &&
                                calculatedProfitMargin > 1 && (
                                  <Typography
                                    variant="body2"
                                    color="warning.dark"
                                    sx={{ mt: 1 }}
                                  >
                                    ⚠️ Profit margin seems unusually high
                                  </Typography>
                                )}
                            </Box>
                          </Grid>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Radio Button for Buying or Selling Unit */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            Is the New Unit Buying or Selling?
                          </Typography>
                          <FormControl component="fieldset">
                            <RadioGroup
                              row
                              value={unitCategory}
                              onChange={(e) => {
                                const value = e.target.value;
                                setUnitCategory(value);
                                if (value !== "buying") {
                                  setSelectedSupplierIds([]);
                                  setPrimarySupplierId("");
                                  setSupplierError("");
                                }
                              }}
                              disabled={formFieldsDisabled}
                            >
                              <FormControlLabel
                                value="buying"
                                control={<Radio />}
                                label="Buying"
                              />
                              <FormControlLabel
                                value="selling"
                                control={<Radio />}
                                label="Selling"
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Current Buying Units:{" "}
                            {existingUnits.filter(
                              (unit) => unit.unit_category === "buying",
                            ).length > 0
                              ? existingUnits
                                  .filter(
                                    (unit) => unit.unit_category === "buying",
                                  )
                                  .map((unit) => unit.unit_type)
                                  .join(", ")
                              : "None"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Current Selling Units:{" "}
                            {existingUnits.filter(
                              (unit) => unit.unit_category === "selling",
                            ).length > 0
                              ? existingUnits
                                  .filter(
                                    (unit) => unit.unit_category === "selling",
                                  )
                                  .map((unit) => unit.unit_type)
                                  .join(", ")
                              : "None"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            freeSolo
                            options={
                              Array.isArray(searchUnitTypes)
                                ? searchUnitTypes
                                : []
                            }
                            value={newUnitType}
                            onChange={handleNewUnitTypeChange}
                            disabled={formFieldsDisabled}
                            onInputChange={(event, newInputValue) => {
                              setNewUnitType(newInputValue);
                              if (buyingUnitError) {
                                setBuyingUnitError(false);
                                setUnitTypeError("");
                              }
                            }}
                            onOpen={() => {
                              console.log("Autocomplete onOpen: New Unit Type");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="New Unit Type"
                                variant="outlined"
                                fullWidth
                                required
                                error={buyingUnitError}
                                helperText={
                                  buyingUnitError
                                    ? "Duplicate Unit Type entered - Please enter a different Unit Type"
                                    : "Search from all buying and selling units used by any user for this product"
                                }
                                inputRef={buyingUnitRef}
                              />
                            )}
                          />
                        </Grid>
                        {unitCategory === "buying" && (
                          <>
                            <Grid item xs={12}>
                              <FormControl fullWidth error={!!supplierError}>
                                <InputLabel id="suppliers-multi-label-new">
                                  Suppliers (optional, multiple)
                                </InputLabel>
                                <Select
                                  labelId="suppliers-multi-label-new"
                                  label="Suppliers (optional, multiple)"
                                  multiple
                                  value={selectedSupplierIds}
                                  onChange={handleSuppliersChange}
                                  disabled={formFieldsDisabled}
                                  renderValue={(selected) => {
                                    const names = suppliers
                                      .filter((s) =>
                                        selected.includes(
                                          String(s.supplier_id),
                                        ),
                                      )
                                      .map(
                                        (s) =>
                                          s.name ||
                                          s.supplier_name ||
                                          "Unnamed",
                                      );
                                    return names.join(", ");
                                  }}
                                >
                                  {suppliers.map((s) => (
                                    <MenuItem
                                      key={s.supplier_id}
                                      value={String(s.supplier_id)}
                                    >
                                      {s.name || s.supplier_name || "Unnamed"}
                                    </MenuItem>
                                  ))}
                                  <MenuItem value={ADD_NEW_SUPPLIER_VALUE}>
                                    <em>Add New Supplier...</em>
                                  </MenuItem>
                                </Select>
                                {supplierError && (
                                  <FormHelperText>
                                    {supplierError}
                                  </FormHelperText>
                                )}
                              </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="subtitle2">
                                Most Recent Supplier
                              </Typography>
                              <RadioGroup
                                value={primarySupplierId}
                                onChange={(e) =>
                                  setPrimarySupplierId(e.target.value)
                                }
                                disabled={formFieldsDisabled}
                              >
                                {selectedSupplierIds.length === 0 ? (
                                  <FormHelperText>
                                    Select at least one supplier to choose a
                                    primary.
                                  </FormHelperText>
                                ) : (
                                  selectedSupplierIds.map((sid) => {
                                    const sup = suppliers.find(
                                      (s) => String(s.supplier_id) === sid,
                                    );
                                    const label = sup
                                      ? (sup.name ||
                                          sup.supplier_name ||
                                          "Unnamed") +
                                        (sup.market_name
                                          ? ` (${sup.market_name})`
                                          : "")
                                      : sid;
                                    return (
                                      <FormControlLabel
                                        key={sid}
                                        value={sid}
                                        control={<Radio />}
                                        label={label}
                                      />
                                    );
                                  })
                                )}
                              </RadioGroup>
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12}>
                          <TextField
                            label={
                              unitCategory === "buying"
                                ? "Current Order Price of New Unit"
                                : "Current Retail Price of New Unit"
                            }
                            variant="outlined"
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            value={
                              unitCategory === "buying"
                                ? orderPrice
                                : retailPrice
                            }
                            onChange={(e) => {
                              if (unitCategory === "buying") {
                                setOrderPrice(e.target.value);
                              } else {
                                setRetailPrice(e.target.value);
                              }
                            }}
                            disabled={formFieldsDisabled}
                            helperText={`Price per ${newUnitType || "new unit"}`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            label="Select Existing Unit for Comparison"
                            variant="outlined"
                            fullWidth
                            value={selectedExistingUnit}
                            onChange={(e) =>
                              setSelectedExistingUnit(e.target.value)
                            }
                            required
                            disabled={formFieldsDisabled}
                          >
                            {existingUnits.map((unit) => (
                              <MenuItem key={unit.unit_id} value={unit.unit_id}>
                                {unit.unit_type} ({unit.unit_category})
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label={`Please Enter How many ${existingUnits.find((unit) => unit.unit_id === selectedExistingUnit)?.unit_type || "selected unit type"} are there in ${newUnitType || "new unit type"}?`}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{
                              style: {
                                whiteSpace: "normal",
                                fontSize: "0.875rem",
                              },
                            }}
                            value={conversionRate}
                            onChange={(e) => setConversionRate(e.target.value)}
                            required
                            disabled={formFieldsDisabled}
                          />
                        </Grid>
                        {/* Real-time Profit Margin Display for New Unit */}
                        {retailPrice && orderPrice && conversionRate && (
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor:
                                  calculatedProfitMargin !== null &&
                                  (calculatedProfitMargin < 0 ||
                                    calculatedProfitMargin > 1)
                                    ? "warning.light"
                                    : "info.light",
                                borderRadius: 1,
                                border: 1,
                                borderColor:
                                  calculatedProfitMargin !== null &&
                                  (calculatedProfitMargin < 0 ||
                                    calculatedProfitMargin > 1)
                                    ? "warning.main"
                                    : "info.main",
                              }}
                            >
                              {(() => {
                                const comparisonUnit = existingUnits.find(
                                  (unit) => unit.unit_id === selectedExistingUnit,
                                );
                                const comparisonUnitName =
                                  comparisonUnit?.unit_type || "comparison unit";
                                const newUnitName = newUnitType || "new unit";

                                return (
                                  <>
                                    <Typography variant="subtitle2" gutterBottom>
                                      <strong>Profit Margin Calculation:</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                      {unitCategory === "buying"
                                        ? `Cost per ${comparisonUnitName}: ${retailPrice && conversionRate ? (parseFloat(orderPrice) / parseFloat(conversionRate)).toFixed(2) : "N/A"}`
                                        : `Cost per ${newUnitName}: ${orderPrice && conversionRate ? (parseFloat(orderPrice) / parseFloat(conversionRate)).toFixed(2) : "N/A"}`}
                                    </Typography>
                                    <Typography variant="body2">
                                      {unitCategory === "buying"
                                        ? `Profit per ${comparisonUnitName}: ${retailPrice && orderPrice && conversionRate ? (parseFloat(retailPrice) - parseFloat(orderPrice) / parseFloat(conversionRate)).toFixed(2) : "N/A"}`
                                        : `Profit per ${newUnitName}: ${retailPrice && orderPrice && conversionRate ? (parseFloat(retailPrice) - parseFloat(orderPrice) / parseFloat(conversionRate)).toFixed(2) : "N/A"}`}
                                    </Typography>
                                  </>
                                );
                              })()}
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                Profit Margin: {" "}
                                {calculatedProfitMargin !== null
                                  ? `${(calculatedProfitMargin * 100).toFixed(1)}%`
                                  : "N/A"}
                              </Typography>
                              {calculatedProfitMargin !== null &&
                                calculatedProfitMargin < 0 && (
                                  <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ mt: 1 }}
                                  >
                                    ⚠️ This results in a loss
                                  </Typography>
                                )}
                              {calculatedProfitMargin !== null &&
                                calculatedProfitMargin > 1 && (
                                  <Typography
                                    variant="body2"
                                    color="warning.dark"
                                    sx={{ mt: 1 }}
                                  >
                                    ⚠️ Profit margin seems unusually high
                                  </Typography>
                                )}
                            </Box>
                          </Grid>
                        )}
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    data-testid="unit-form-save"
                  >
                    Add Unit
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={unitTypeError ? 6000 : 4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Add Supplier Confirmation Dialog */}
      <Dialog
        open={addSupplierDialogOpen}
        onClose={handleAddSupplierDialogClose}
      >
        <DialogTitle sx={{ textAlign: "center" }}>Add New Supplier</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to add a new supplier now? You will be taken to the Add
            Supplier page. When you return, your current changes on this page
            will be restored and the new supplier will be selected.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button onClick={handleAddSupplierDialogClose} autoFocus>
            No
          </Button>
          <Button
            onClick={handleConfirmAddSupplier}
            color="primary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profit Margin Warning Dialog */}
      <Dialog
        open={profitWarningOpen}
        onClose={() => setProfitWarningOpen(false)}
      >
        <DialogTitle>Profit Margin Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {calculatedProfitMargin < 0
              ? `The current pricing results in a loss of ${Math.abs(calculatedProfitMargin * 100).toFixed(1)}% per unit sold.`
              : `The current pricing results in a profit margin of ${(calculatedProfitMargin * 100).toFixed(1)}%, which seems unusually high.`}
            <br />
            <br />
            <strong>Current Calculation:</strong>
            <br />
            {isAddingNewUnit ? (
              <>
                New Unit Type: {newUnitType} ({unitCategory})<br />
                Existing Unit:{" "}
                {
                  existingUnits.find(
                    (unit) => unit.unit_id === selectedExistingUnit,
                  )?.unit_type
                }{" "}
                (
                {
                  existingUnits.find(
                    (unit) => unit.unit_id === selectedExistingUnit,
                  )?.unit_category
                }
                )<br />
                {unitCategory === "buying" ? "Order" : "Retail"} Price (New
                Unit): {unitCategory === "buying" ? orderPrice : retailPrice}
                <br />
                {unitCategory === "buying" ? "Retail" : "Order"} Price (Existing
                Unit): {unitCategory === "buying" ? retailPrice : orderPrice}
                <br />
              </>
            ) : (
              <>
                Retail Price: {retailPrice}
                <br />
                Order Price: {orderPrice}
                <br />
              </>
            )}
            Conversion Rate: {conversionRate}
            <br />
            Cost per Selling Unit:{" "}
            {orderPrice && conversionRate
              ? (parseFloat(orderPrice) / parseFloat(conversionRate)).toFixed(2)
              : "N/A"}
            <br />
            Profit per Unit:{" "}
            {retailPrice && orderPrice && conversionRate
              ? (
                  parseFloat(retailPrice) -
                  parseFloat(orderPrice) / parseFloat(conversionRate)
                ).toFixed(2)
              : "N/A"}
            <br />
            <br />
            Would you like to proceed anyway or update the conversion rate?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfitWarningOpen(false)} color="primary">
            Update Conversion Rate
          </Button>
          <Button
            onClick={async () => {
              setProfitWarningOpen(false);
              await submitForm(pendingFormData);
            }}
            color="primary"
            variant="contained"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Another Unit Dialog */}
      <Dialog
        open={addAnotherDialogOpen}
        onClose={() => setAddAnotherDialogOpen(false)}
      >
        <DialogTitle>Add Additional Units?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Would you like to add additional units of sale or purchase to "
            {currentProductInfo?.product_name}" other than{" "}
            {firstTimeSellingUnit && firstTimeBuyingUnit
              ? `${firstTimeSellingUnit} and ${firstTimeBuyingUnit}`
              : "the existing units"}
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFinishAddingUnits} color="primary">
            No, Proceed to Stock Entry
          </Button>
          <Button
            onClick={handleAddAnotherUnit}
            color="primary"
            variant="contained"
          >
            Yes, Add Additional Units
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddUnit;
